/**
 * Exact decimal arithmetic for unit converters.
 *
 * Energy and pressure units are defined by exact decimal factors (1 thermochemical calorie
 * is exactly 4.184 J, 1 standard atmosphere exactly 101 325 Pa) and the metric prefixes by
 * exact powers of ten. Floating point turns those into numbers like 4.183999999999999, so
 * the converters here work in ratios of `bigint`s and round only when a result is written
 * out. The conversions then agree with NIST Special Publication 811 to every digit the
 * publication gives, and a round trip returns the number that went in.
 */

import { normalizeNumberInput } from "@/lib/parse-number";

export interface Ratio {
  /** Numerator; carries the sign. */
  n: bigint;
  /** Denominator; always positive. */
  d: bigint;
}

const ZERO = BigInt(0);
const ONE = BigInt(1);
const TWO = BigInt(2);
const TEN = BigInt(10);

/** The longest typed number accepted: digits in the mantissa. */
export const MAX_DIGITS = 40;
/** The largest power of ten accepted in a typed exponent. */
export const MAX_EXPONENT = 300;

function gcd(a: bigint, b: bigint): bigint {
  let x = a < ZERO ? -a : a;
  let y = b < ZERO ? -b : b;
  while (y !== ZERO) {
    [x, y] = [y, x % y];
  }
  return x;
}

export function ratio(n: bigint, d: bigint = ONE): Ratio {
  const divisor = gcd(n, d) || ONE;
  const sign = d < ZERO ? -ONE : ONE;
  return { n: (sign * n) / divisor, d: (sign * d) / divisor };
}

export function multiply(a: Ratio, b: Ratio): Ratio {
  return ratio(a.n * b.n, a.d * b.d);
}

export function divide(a: Ratio, b: Ratio): Ratio {
  return ratio(a.n * b.d, a.d * b.n);
}

export function isZero(a: Ratio): boolean {
  return a.n === ZERO;
}

export function powerOfTen(exponent: number): Ratio {
  return exponent >= 0
    ? ratio(TEN ** BigInt(exponent))
    : ratio(ONE, TEN ** BigInt(-exponent));
}

const TRAILING_ZEROS = /0+$/;
const PLAIN = /^([+-]?)(\d*)(?:\.(\d*))?$/;

/** A decimal written with a point, such as `4.184` or `-0.0254`, as an exact ratio. */
export function decimal(text: string): Ratio {
  const match = PLAIN.exec(text);
  if (!(match && (match[2] || match[3]))) {
    throw new Error(`not a decimal: ${text}`);
  }
  const fraction = match[3] ?? "";
  const n = BigInt(`${match[2] || "0"}${fraction}`);
  return ratio(match[1] === "-" ? -n : n, TEN ** BigInt(fraction.length));
}

/** `1.5e-9`, `1.5E9`, `1.5×10^-9`, `1.5 x 10^9`, `1.5*10^9` split into mantissa and exponent. */
const SCIENTIFIC = /^(.+?)\s*(?:[eE]|[x×*]\s*10\s*\^?\s*)\s*([+−-]?\s*\d+)$/;

/**
 * A number as a person types it: decimal point or comma, optional grouping, optional
 * exponent. Returns null when it is not a number or is too long to trust.
 */
export function parseQuantity(raw: string): Ratio | null {
  const text = raw.trim();
  let mantissaText = text;
  let exponent = 0;
  const scientific = SCIENTIFIC.exec(text);
  if (scientific) {
    mantissaText = scientific[1] ?? "";
    const exponentText = (scientific[2] ?? "")
      .replace(/\s/g, "")
      .replace("−", "-");
    exponent = Number(exponentText);
    if (!Number.isInteger(exponent) || Math.abs(exponent) > MAX_EXPONENT) {
      return null;
    }
  }
  const normalized = normalizeNumberInput(mantissaText);
  if (
    normalized === null ||
    normalized.replace(/\D/g, "").length > MAX_DIGITS
  ) {
    return null;
  }
  return multiply(decimal(normalized), powerOfTen(exponent));
}

export interface Written {
  negative: boolean;
  /** Significant digits without trailing zeros; "0" for zero. */
  digits: string;
  /** Power of ten of the first digit: 1234 is digits "1234", exponent 3. */
  exponent: number;
}

/** Rounds a ratio to a number of significant digits (half up on the magnitude). */
export function toSignificant(value: Ratio, significant: number): Written {
  if (value.n === ZERO) {
    return { negative: false, digits: "0", exponent: 0 };
  }
  const negative = value.n < ZERO;
  const n = negative ? -value.n : value.n;
  let exponent = n.toString().length - value.d.toString().length;
  // The estimate can be one too high; correct it so that 10^exponent <= |value| < 10^(exponent + 1).
  if (compareToPowerOfTen(n, value.d, exponent) < 0) {
    exponent -= 1;
  }
  const shift = significant - 1 - exponent;
  const scaledN = shift >= 0 ? n * TEN ** BigInt(shift) : n;
  const scaledD = shift >= 0 ? value.d : value.d * TEN ** BigInt(-shift);
  let rounded = (TWO * scaledN + scaledD) / (TWO * scaledD);
  if (rounded.toString().length > significant) {
    rounded /= TEN;
    exponent += 1;
  }
  const digits = rounded.toString().replace(TRAILING_ZEROS, "") || "0";
  return { negative, digits, exponent };
}

/** Compares n/d with 10^exponent: -1, 0 or 1. */
function compareToPowerOfTen(n: bigint, d: bigint, exponent: number): number {
  const left = exponent >= 0 ? n : n * TEN ** BigInt(-exponent);
  const right = exponent >= 0 ? d * TEN ** BigInt(exponent) : d;
  if (left === right) {
    return 0;
  }
  return left < right ? -1 : 1;
}

/** Whether the rounded digits equal the value exactly (no rounding took place). */
export function isExact(value: Ratio, written: Written): boolean {
  if (value.n === ZERO) {
    return true;
  }
  const mantissa = BigInt(written.digits);
  const scale = written.exponent - (written.digits.length - 1);
  const target =
    scale >= 0
      ? ratio((written.negative ? -mantissa : mantissa) * TEN ** BigInt(scale))
      : ratio(written.negative ? -mantissa : mantissa, TEN ** BigInt(-scale));
  return target.n === value.n && target.d === value.d;
}

/** Plain decimal text with the given decimal mark, or null when it would run past `limit` places. */
export function plainText(
  written: Written,
  mark = ".",
  limit = 21
): string | null {
  const { digits, exponent, negative } = written;
  if (digits === "0") {
    return "0";
  }
  if (exponent > limit || exponent < -limit) {
    return null;
  }
  let body: string;
  if (exponent >= digits.length - 1) {
    body = digits + "0".repeat(exponent - (digits.length - 1));
  } else if (exponent >= 0) {
    body = `${digits.slice(0, exponent + 1)}${mark}${digits.slice(exponent + 1)}`;
  } else {
    body = `0${mark}${"0".repeat(-exponent - 1)}${digits}`;
  }
  return negative ? `-${body}` : body;
}

/** `1.234 × 10^-5` as text parts: the mantissa with its mark, and the exponent. */
export function scientificParts(
  written: Written,
  mark = "."
): { mantissa: string; exponent: number } {
  const { digits, negative } = written;
  const head = digits.slice(0, 1);
  const tail = digits.slice(1);
  const mantissa = `${negative ? "-" : ""}${head}${tail ? `${mark}${tail}` : ""}`;
  return { mantissa, exponent: digits === "0" ? 0 : written.exponent };
}

/** The same in machine form (`1.234e-5`), for copying into a spreadsheet or a program. */
export function machineText(written: Written): string {
  const { mantissa, exponent } = scientificParts(written);
  return `${mantissa}e${exponent}`;
}
